import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWidgetApi } from '@matrix-widget-toolkit/react';
import API_URLS from '../config.ts';
import { Client } from 'xrpl';
import { trimRoomId } from './utils';
import {
  LockCondition,
  RoomSettings,
  NFTSubRule,
  TokenSubRule,
  LogicOp,
} from './types';

import {
  Container,
  Paper,
  Box,
  Typography,
  Divider,
  Alert,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardMedia,
  Avatar,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
} from '@mui/material';

import { Loader2 } from 'lucide-react';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import SearchIcon from '@mui/icons-material/Search';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

// ---------- Local helper types ----------
type GroupedNFTs = Record<
  string,
  {
    nfts: any[];
    // union of attribute keys discovered across NFTs in this (issuer:taxon) collection
    attrKeys?: string[];
  }
>;
type TokenLine = { 
  issuer: string; 
  currency: string; 
  balance?: string; 
  limit?: string;
  // Enhanced metadata from Bithomp
  icon?: string | null;
  name?: string;
  symbol?: string | null;
  isLpToken?: boolean;
  description?: string | null;
  stablecoin?: boolean;
  kyc?: boolean;
  trustlines?: number;
  rating?: number;
};

type AttrRow = { id: string; key: string; value: string };

// A safe empty condition so hooks can always run without branching
const EMPTY_MULTI_LOCK: LockCondition = {
  id: `lock-${Date.now()}`,
  type: 'lock',
  globalLogic: 'OR',
  tokenLogic: 'OR',
  nftLogic: 'AND',
  issuer: '',
  taxon: '',
  nftCount: 1,
  nftImageUrl: '',
  rules: [],
};

const FALLBACK_BITHOMP = '0b833219-c387-4b3f-9606-0e4bd82e5862';

function toGateway(url?: string) {
  if (!url) return '';
  if (url.startsWith('ipfs://')) {
    return url.replace('ipfs://', 'https://ipfs.io/ipfs/').replace('#', '%23');
  }
  return url;
}

function humanizeCurrencyCode(currency: string): string {
  if (!currency) return '';
  if (/^[A-Za-z0-9]{3,12}$/.test(currency)) {
    return currency.trim();
  }

  if (/^[0-9A-F]{40}$/i.test(currency)) {
    try {
      const bytes = currency.match(/.{2}/g)?.map((hex) => parseInt(hex, 16)) ?? [];
      const decoded = String.fromCharCode(...bytes).replace(/\u0000+$/g, '').trim();
      if (decoded && /[A-Za-z0-9]/.test(decoded)) {
        return decoded;
      }
    } catch (err) {
      console.warn('Failed to decode currency hex', currency, err);
    }
  }

  return currency;
}

async function fetchTokenMetadata(issuer: string, currency: string): Promise<TokenLine> {
  const decodedSymbol = humanizeCurrencyCode(currency);
  const base: TokenLine = {
    issuer,
    currency,
    name: decodedSymbol || currency,
    symbol: decodedSymbol || currency,
    icon: null,
  };

  if (!issuer || !currency) {
    return base;
  }

  try {
    const metadataUrl = `${API_URLS.marketPlace}/api/v2/trustlines/tokens?currency=${encodeURIComponent(currency)}&currencyDetails=true`;
    const metadataRes = await fetch(metadataUrl, {
      method: 'GET',
      headers: {
        'x-bithomp-token': API_URLS.bithompToken || FALLBACK_BITHOMP,
      },
    });

    if (metadataRes.ok) {
      const data = await metadataRes.json();
      const tokenMetadata = data.tokens?.find((token: any) => token.issuer === issuer) ?? data.tokens?.[0];

      if (tokenMetadata) {
        const friendlyName = tokenMetadata.name
          || tokenMetadata.currencyDetails?.name
          || tokenMetadata.currencyDetails?.currency
          || base.name;

        const friendlySymbol = tokenMetadata.currencyDetails?.currency
          || tokenMetadata.currency
          || base.symbol;

        return {
          ...base,
          name: friendlyName,
          symbol: friendlySymbol,
          icon: tokenMetadata.icon || null,
          description: tokenMetadata.description || null,
          isLpToken: tokenMetadata.lp_token || false,
          stablecoin: tokenMetadata.stablecoin || false,
          kyc: tokenMetadata.kyc || false,
          trustlines: tokenMetadata.trustlines || 0,
          rating: tokenMetadata.rating || 0,
        };
      }
    }
  } catch (error) {
    console.error(`Error fetching token metadata for ${issuer}:${currency}:`, error);
  }

  return base;
}

function isTokenRule(rule: NFTSubRule | TokenSubRule): rule is TokenSubRule {
  const kind = (rule as any)?.kind;
  const isNFT = kind ? kind === 'NFT' : (rule as any).taxon != null;
  return !isNFT && Boolean(rule?.issuer) && Boolean((rule as TokenSubRule)?.currency);
}

function extractAttributeKeysFromMetadata(metadata: any): string[] {
  if (!metadata) return [];
  const keys = new Set<string>();
  // OpenSea-style list: [{ trait_type, value }]
  if (Array.isArray(metadata.attributes)) {
    for (const a of metadata.attributes) {
      const name = a?.trait_type ?? a?.traitType ?? a?.name ?? a?.key;
      if (name) keys.add(String(name));
    }
  }
  // Some collections put attributes as a flat object
  if (metadata.attributes && !Array.isArray(metadata.attributes) && typeof metadata.attributes === 'object') {
    for (const k of Object.keys(metadata.attributes)) keys.add(k);
  }
  return Array.from(keys);
}

// Component for non-admin users to view room rules
const RoomRulesViewer: React.FC = () => {
  const [roomSettings, setRoomSettings] = useState<RoomSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [tokenMetadataMap, setTokenMetadataMap] = useState<Record<string, TokenLine>>({});
  const widgetApi = useWidgetApi();
  const apiUrl = API_URLS.backendUrl;

  useEffect(() => {
    const fetchRoomData = async () => {
      try {
        const rawRoomId = widgetApi.widgetParameters.roomId;
        if (!rawRoomId) return;
        
        const trimmedRoomId = trimRoomId(rawRoomId);
        
        // Fetch room settings
        const response = await fetch(`${apiUrl}/api/admin/settings`, {
          method: 'POST',
          headers: {
            Authorization: 'Bearer 1234567890QWERTYUIOP',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ roomId: encodeURIComponent(trimmedRoomId) }),
        });
        
        if (response.ok) {
          const data = await response.json();
          let tree = data.tree ?? EMPTY_MULTI_LOCK;
          if (typeof tree === 'string') {
            try { 
              tree = JSON.parse(tree); 
            } catch { 
              tree = EMPTY_MULTI_LOCK; 
            }
          }
    setRoomSettings({ tree, kickMessage: data.kickMessage || '', active: data.active ?? true });
        }

        // Fetch room name
        const roomEvents = await widgetApi.receiveStateEvents('m.room.name');
        if (roomEvents && roomEvents.length > 0) {
          const content = roomEvents[0].content as { name?: string };
          setDisplayName(content.name || 'This Room');
        }
      } catch (error) {
        console.error('Error fetching room data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoomData();
  }, [widgetApi, apiUrl]);

  useEffect(() => {
    const condition = roomSettings?.tree as LockCondition;
    const ruleList = Array.isArray(condition?.rules) ? condition.rules : [];
    const tokenRules = ruleList.filter(isTokenRule);

    if (!tokenRules.length) {
      setTokenMetadataMap({});
      return;
    }

    let cancelled = false;
  const uniqueKeys = Array.from(new Set(tokenRules.map((r) => `${r.issuer}:${r.currency}`)));

    const load = async () => {
      const entries = await Promise.all(
        uniqueKeys.map(async (key) => {
          const [issuer, currency = ''] = key.split(':');
          const meta = await fetchTokenMetadata(issuer, currency);
          return [key, meta] as const;
        })
      );

      if (!cancelled) {
        setTokenMetadataMap(Object.fromEntries(entries));
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [roomSettings]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <Box sx={{ py: 6, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} />
            <Typography>Loading room rules...</Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  const tree = roomSettings?.tree as LockCondition;
  const rules = Array.isArray(tree?.rules) ? tree.rules : [];
  const hasRules = rules.length > 0;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Access Requirements {displayName ? `for ${displayName}` : ''}
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom sx={{ maxWidth: 600, textAlign: 'center' }}>
            {hasRules 
              ? 'This room has token gate requirements. You must meet the following conditions to access this room:'
              : 'This room currently has no access restrictions.'
            }
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {hasRules ? (
          <Box
            sx={{
              p: 3,
              border: 2,
              borderColor: 'primary.200',
              borderRadius: 3,
              bgcolor: 'primary.50',
              boxShadow: 2,
            }}
          >
            <Typography variant="h6" gutterBottom color="primary.main" fontWeight={700}>
              Token Gate Requirements
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {tree.globalLogic === 'AND'
                ? 'You must satisfy BOTH the Token requirements and the NFT requirements (AND).'
                : 'You may satisfy EITHER the Token requirements or the NFT requirements (OR).'}
            </Typography>

            <Stack spacing={2}>
              {rules.map((r: any, idx: number) => {
                const isNFT = r.kind ? r.kind === 'NFT' : r.taxon != null;
                if (isNFT) {
                  const filters = Array.isArray(r.attributeFilters) ? r.attributeFilters : [];
                  return (
                    <Card key={`NFT-${r.issuer}:${r.taxon}:${idx}`} sx={{ p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'grey.300' }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        {r.nftImageUrl ? (
                          <Avatar variant="rounded" src={r.nftImageUrl} sx={{ width: 48, height: 48, border: 2, borderColor: 'primary.main' }} />
                        ) : (
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: 'grey.200',
                              borderRadius: 2,
                              border: 2,
                              borderColor: 'grey.400',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              NFT
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" fontWeight={700} color="primary.main">
                            {r.collectionName || 'NFT Collection'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Minimum required: <strong>{r.nftCount} NFT{r.nftCount > 1 ? 's' : ''}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Issuer: {r.issuer}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Taxon: {r.taxon}
                          </Typography>
                          {filters.length > 0 && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="caption" color="primary.main" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                Additional Requirements:
                              </Typography>
                              {filters.map((f: any, i: number) => (
                                <Typography key={`${idx}-f-${i}`} variant="caption" color="text.primary" sx={{ display: 'block' }}>
                                  • {f.key}: <strong>{String(f.value)}</strong>
                                </Typography>
                              ))}
                              {filters.length > 1 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  Logic: <strong>{r.attributeLogic || 'AND'}</strong> (must meet {r.attributeLogic === 'AND' ? 'all' : 'any'} requirements)
                                </Typography>
                              )}
                            </Box>
                          )}
                          {/* Legacy single attribute support */}
                          {!filters.length && r.attributeKey && (r.attributeValue ?? '') !== '' && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="caption" color="primary.main" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                Additional Requirement:
                              </Typography>
                              <Typography variant="caption" color="text.primary" sx={{ display: 'block' }}>
                                • {r.attributeKey}: <strong>{String(r.attributeValue)}</strong>
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    </Card>
                  );
                } else {
                  const tokenKey = `${r.issuer}:${r.currency}`;
                  const tokenMeta = tokenMetadataMap[tokenKey];
                  const tokenIcon = r.tokenIcon || tokenMeta?.icon || null;
                  const tokenLabel = r.tokenName || tokenMeta?.name || humanizeCurrencyCode(r.currency);
                  const tokenSymbol = r.tokenSymbol || tokenMeta?.symbol || humanizeCurrencyCode(r.currency);

                  return (
                    <Card
                      key={`TOK-${r.issuer}:${r.currency}:${idx}`}
                      sx={{ p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'grey.300' }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                        {tokenIcon ? (
                          <Avatar
                            src={tokenIcon}
                            variant="rounded"
                            sx={{ width: 48, height: 48, border: 1, borderColor: 'grey.300' }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: 'grey.200',
                              color: 'text.secondary',
                              fontWeight: 700,
                              border: 1,
                              borderColor: 'grey.300',
                            }}
                          >
                            {String(tokenSymbol || tokenLabel || r.currency).slice(0, 4)}
                          </Avatar>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
                            {tokenLabel}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Minimum required: <strong>{r.minAmount} {tokenSymbol || tokenLabel}</strong>
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', wordBreak: 'break-all' }}
                          >
                            Issuer: {r.issuer}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                            title={r.currency}
                          >
                            Currency Code: {tokenSymbol || tokenLabel}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  );
                }
              })}
            </Stack>

            {roomSettings?.kickMessage && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.50', border: 1, borderColor: 'warning.200', borderRadius: 2 }}>
                <Typography variant="body2" color="warning.dark" fontWeight={600} sx={{ mb: 1 }}>
                  Important Notice:
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {roomSettings.kickMessage}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              border: 2,
              borderColor: 'success.200',
              borderRadius: 3,
              bgcolor: 'success.50',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="success.main" fontWeight={700} gutterBottom>
              No Access Restrictions
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This room is open to all members. No token gate requirements are currently configured.
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export const NFTAdmin: React.FC = () => {
  // ====== Permission / Matrix gate ======
  const [checkedPowerLevels, setCheckedPowerLevels] = useState(0);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const widgetApi = useWidgetApi();

  useEffect(() => {
    console.log('widgetApi widgetParameters:', widgetApi.widgetParameters);
    // The user who has power_level > 100 can only access this Widget
    const loadData = async () => {
      try {
        const curUserId = widgetApi.widgetParameters.userId || '';
        
        setCurrentUserId(curUserId);
        console.log('widgetApi widgetParameters:', widgetApi.widgetParameters);
        console.log('Current user ID:', curUserId);

        // Use Widget API to get power levels instead of bot credentials
        const powerLevelEvents = await widgetApi.receiveStateEvents('m.room.power_levels');
        console.log('Power level events:', powerLevelEvents);

        if (powerLevelEvents && powerLevelEvents.length > 0) {
          const currentPowerLevels = powerLevelEvents[0].content as {
            users?: Record<string, number>;
            users_default?: number;
          };
          console.log('Current power levels:', currentPowerLevels);

          const userPower = currentPowerLevels.users?.[curUserId] ?? currentPowerLevels.users_default ?? 0;
          console.log('User', curUserId, 'power level:', userPower);

          if (userPower >= 100) {
            setCheckedPowerLevels(2); // Set to 2 to indicate access granted
            console.log('User has sufficient power level to access the widget.');
          } else {
            setCheckedPowerLevels(1); // Set to 1 to indicate no access
            console.log('User does not have sufficient power level to access the widget.');
          }
        } else {
          console.warn('No power level events found');
          setCheckedPowerLevels(1); // Set to 1 to indicate no access
        }
      } catch (error) {
        console.error('Error loading power levels:', error);
        setCheckedPowerLevels(1); // Set to 1 to indicate no access
        return;
      }
    }

    loadData();
    setCheckedPowerLevels(0);
  }, [widgetApi]);

  // ====== Core settings / persistence ======
  const apiUrl = API_URLS.backendUrl;
  const secretKey = process.env.ENCRYPTION_KEY || 'default_secret_key';

  const [savedConditionTree, setSavedConditionTree] = useState<any | null>(null);
  const [editingBasic, setEditingBasic] = useState<LockCondition | null>(null);
  const [kickMessage, setKickMessage] = useState<string>('');
  const [savedMessage, setSavedMessage] = useState(false);
  const [saveRequestResult, setSaveRequestResult] = useState(0); // 0 idle, 1 pending, 2 success
  const [saveError, setSaveError] = useState<string | null>(null);
  const [botJoinError, setBotJoinError] = useState<string>('');
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isGateActive, setIsGateActive] = useState<boolean>(true);
  const [isUpdatingGateActive, setIsUpdatingGateActive] = useState<boolean>(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState<boolean>(false);
  const [activeToggleError, setActiveToggleError] = useState<string | null>(null);

  const fetchConditionTree = async (): Promise<RoomSettings | null> => {
    const rawRoomId = widgetApi.widgetParameters.roomId;
    if (!rawRoomId) return null;
    const trimmedRoomId = trimRoomId(rawRoomId);
    try {
      const response = await fetch(`${apiUrl}/api/admin/settings`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer 1234567890QWERTYUIOP',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ roomId: encodeURIComponent(trimmedRoomId) }),
      });
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      const tree = data.tree ?? EMPTY_MULTI_LOCK;
  return { tree, kickMessage: data.kickMessage || '', active: data.active ?? true };
    } catch (error) {
      console.error('Fetch error:', error);
      return null;
    }
  };

  const initializeTree = useCallback(async () => {
    try {
  const defaultSettings: RoomSettings = { tree: EMPTY_MULTI_LOCK, kickMessage: '', active: true };
      const fetchedSettings = await fetchConditionTree();
      const settingsToUse = fetchedSettings ?? defaultSettings;

      let parsed: any = settingsToUse.tree;
      if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed); } catch { parsed = defaultSettings.tree; }
      }

      // migrate defaults
      parsed.globalLogic = parsed.globalLogic || 'OR';
      parsed.tokenLogic = parsed.tokenLogic || 'OR';
      parsed.nftLogic = parsed.nftLogic || parsed.logic || 'AND';

      setSavedConditionTree(parsed);
      setKickMessage(settingsToUse.kickMessage || '');
      setEditingBasic(parsed as LockCondition);
      setIsGateActive(settingsToUse.active ?? true);
    } catch (error) {
      console.error('Error initializing tree:', error);
    }
  }, []);

  useEffect(() => {
    initializeTree();

    const fetchRoomName = async () => {
      try {
        const rawRoomId = widgetApi.widgetParameters.roomId;
        if (!rawRoomId) return;
        const roomEvents = await widgetApi.receiveStateEvents('m.room.name');
        if (roomEvents && roomEvents.length > 0) {
          const content = roomEvents[0].content as { name?: string };
          setDisplayName(content.name || 'Untitled Room');
        }
      } catch (error) {
        console.error('Error fetching roomname:', error);
      }
    };

    fetchRoomName();
  }, [widgetApi, initializeTree]);

  const updateCredits = async (userId: string) => {
    const backend_url = API_URLS.textrpBackend;
    const password = API_URLS.textrpPassword || 'demo123'; // fallback for development
    try {
      const response = await fetch(`${backend_url}/chat-webhook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service: 'token', type: 'gate', address: userId, password }),
      });
      if (!response.ok) {
        const creditError = new Error('Credits Insufficient. Please purchase more credits to save these changes.');
        setSaveError(creditError.message);
        throw creditError;
      }
      return true;
    } catch (error) {
      console.error('Save error:', error);
      const message = error instanceof Error ? error.message : 'Credits Insufficient. Please purchase more credits to save these changes.';
      setSaveError(message);
      throw (error instanceof Error ? error : new Error(message));
    }
  };

  const saveConditionTree = async (updatedTree: LockCondition) => {
    const rawRoomId = widgetApi.widgetParameters.roomId;
    if (!rawRoomId) {
      const errorMsg = 'Room ID not found';
      setSaveError(errorMsg);
      throw new Error(errorMsg);
    }

    const trimmedRoomId = trimRoomId(rawRoomId);
    const userId = (widgetApi.widgetParameters.userId as string) || '';
    try {
      setSaveError(null);
      await updateCredits(userId);

      console.log('Making API call to save settings...', {
        url: `${apiUrl}/api/admin/saveSettings`,
        roomId: trimmedRoomId,
        conditionTree: updatedTree,
        userId: userId,
        kickMessage: kickMessage
      });
      
      const response = await fetch(`${apiUrl}/api/admin/saveSettings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer 1234567890QWERTYUIOP',
        },
        body: JSON.stringify({
          roomId: trimmedRoomId,
          conditionTree: updatedTree,
          accessToken: secretKey,
          userId: userId,
          kickMessage: kickMessage,
          active: isGateActive,
        }),
      });

      console.log('API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('API error response:', errorData);
        throw new Error(`Server responded with ${response.status}: ${errorData}`);
      }

      // Parse response to get any additional info from backend
      const result = await response.json();
      console.log('Save result:', result);

      setSavedConditionTree(updatedTree);
      setSaveRequestResult(2);
      setSavedMessage(true);
      
      // Clear any previous bot join errors
      setBotJoinError('');
      
      // Show bot join error only if backend reports an issue
      if (result.botJoined === false || result.warning) {
        const botJoinMessage = result.warning || 'Configuration saved, but the TokenGate bot could not join the room automatically. Please manually invite @tokengatebot:synapse.textrp.io to this room for the token gate to work properly.';
        setBotJoinError(botJoinMessage);
        // Auto-clear the bot join error after 15 seconds
        setTimeout(() => setBotJoinError(''), 15000);
      }
      
      setTimeout(() => setSavedMessage(false), 3000);
      setTimeout(() => setSaveRequestResult(1), 3000);
    } catch (error) {
      console.error('Save error:', error);
      const message = error instanceof Error ? error.message : 'Failed to save settings';
      setSaveRequestResult(0);
      setSaveError(message);
      if (!message.toLowerCase().includes('credits insufficient')) {
        setTimeout(() => setSaveError(null), 3000);
      }
      // Re-throw the error so it can be caught by onSave
      throw error;
    }
  };

  const updateGateActive = useCallback(async (nextActive: boolean) => {
    const rawRoomId = widgetApi.widgetParameters.roomId;
    if (!rawRoomId) {
      setActiveToggleError('Room ID not found');
      return;
    }

    const trimmedRoomId = trimRoomId(rawRoomId);
    const previousActive = isGateActive;

    setIsUpdatingGateActive(true);
    setActiveToggleError(null);
    setIsGateActive(nextActive);

    try {
      const response = await fetch(`${apiUrl}/api/admin/settings/active`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer 1234567890QWERTYUIOP',
        },
        body: JSON.stringify({
          roomId: encodeURIComponent(trimmedRoomId),
          active: nextActive,
        }),
      });

      if (!response.ok) {
        const raw = await response.text();
        let message = raw;
        try {
          const parsed = JSON.parse(raw);
          message = parsed?.error?.message || JSON.stringify(parsed);
        } catch (parseErr) {
          // Ignore parse error, fallback to raw text
        }
        throw new Error(message || 'Failed to update token gate status');
      }
    } catch (error) {
      setIsGateActive(previousActive);
      const message = error instanceof Error ? error.message : 'Failed to update token gate status';
      setActiveToggleError(message);
      setTimeout(() => setActiveToggleError(null), 8000);
    } finally {
      setIsUpdatingGateActive(false);
    }
  }, [apiUrl, widgetApi, isGateActive]);

  const handleGateToggle = useCallback(() => {
    if (isUpdatingGateActive) return;
    if (isGateActive) {
      setShowDeactivateDialog(true);
    } else {
      updateGateActive(true);
    }
  }, [isGateActive, isUpdatingGateActive, updateGateActive]);

  const handleConfirmDeactivate = useCallback(() => {
    setShowDeactivateDialog(false);
    updateGateActive(false);
  }, [updateGateActive]);

  const handleCancelDeactivate = useCallback(() => {
    setShowDeactivateDialog(false);
  }, []);

  // Compute active tree with safe fallback so we never branch hooks
  const activeConditionTree: LockCondition = useMemo(() => {
    const base = (savedConditionTree ?? (EMPTY_MULTI_LOCK as LockCondition)) as LockCondition;
    if (editingBasic) return editingBasic;
    return base;
  }, [editingBasic, savedConditionTree]);

  const effectiveCondition = useMemo(
    () => (editingBasic || (activeConditionTree as LockCondition) || (EMPTY_MULTI_LOCK as LockCondition)) as LockCondition,
    [editingBasic, activeConditionTree]
  );

  const previewCondition = useMemo(
    () => (savedConditionTree || (EMPTY_MULTI_LOCK as LockCondition)) as LockCondition,
    [savedConditionTree]
  );

  // ====== NFT fetching & selection state ======
  const [NFTs, setNFTs] = useState<GroupedNFTs>({});
  const [manualNFTs, setManualNFTs] = useState<GroupedNFTs>({}); // Manually added collections
  const allNFTs = useMemo(() => ({ ...NFTs, ...manualNFTs }), [NFTs, manualNFTs]);
  const collectionKeys = useMemo(() => Object.keys(allNFTs), [allNFTs]);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [perCollectionCounts, setPerCollectionCounts] = useState<Record<string, number>>({});
  const [logicOp, setLogicOp] = useState<LogicOp>('AND'); // NFT block logic
  const [prepopulated, setPrepopulated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  // ====== Manual collection input state ======
  const [manualIssuer, setManualIssuer] = useState('');
  const [manualTaxon, setManualTaxon] = useState('');
  const [isLoadingManualCollection, setIsLoadingManualCollection] = useState(false);
  const [manualCollectionError, setManualCollectionError] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);

  // Dynamic NFT: per-collection MULTI attribute rows + per-collection AND/OR logic
  const [attrPairs, setAttrPairs] = useState<Record<string, AttrRow[]>>({});
  const [attrLogic, setAttrLogic] = useState<Record<string, LogicOp>>({}); // NEW

  // Legacy preview values (for single-rule)
  const [loadedIssuer, setLoadedIssuer] = useState('');
  const [loadedTaxon, setLoadedTaxon] = useState('');
  const [loadedNftCount, setLoadedNftCount] = useState(1);
  const [loadedNftImageUrl, setLoadedNftImageUrl] = useState<string | null>('');
  const [loadedCollectionName, setLoadedCollectionName] = useState<string | null>('');

  useEffect(() => {
    if (saveRequestResult === 2 && savedConditionTree) {
      const mc = previewCondition as LockCondition;
      setLoadedIssuer((mc as any).issuer || '');
      setLoadedTaxon(((mc as any).taxon as any) || '');
      setLoadedNftCount((mc as any).nftCount || 1);
      setLoadedNftImageUrl((mc as any).nftImageUrl || null);
      setLoadedCollectionName((mc as any).collectionName || null);
    }
  }, [saveRequestResult, previewCondition, savedConditionTree]);

  useEffect(() => {
    const mc = previewCondition as LockCondition;
    if (!mc) return;
    setLoadedIssuer((mc as any).issuer || '');
    setLoadedTaxon(((mc as any).taxon as any) || '');
    setLoadedNftCount((mc as any).nftCount || 1);
    setLoadedNftImageUrl((mc as any).nftImageUrl || null);
    setLoadedCollectionName((mc as any).collectionName || null);
    if (mc.nftLogic) setLogicOp(mc.nftLogic);
  }, [previewCondition]);

  // Fetch NFTs owned by current user (admin)
  useEffect(() => {
    if (!currentUserId) return;
    const ac = new AbortController();

    const fetchNFTs = async () => {
      try {
        const xrplAccount = currentUserId.split('@')[1]?.split(':')[0] || '';
        const url = `${API_URLS.marketPlace}/api/v2/nfts?owner=${xrplAccount}&assets=true&collectionDetails=true&limit=400`;

        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'x-bithomp-token': API_URLS.bithompToken || FALLBACK_BITHOMP,
          },
          signal: ac.signal,
        });
        if (!res.ok) throw new Error('Failed to fetch NFT data');

        const data = await res.json();
        const raw: any[] = data.nfts || [];

        const grouped: GroupedNFTs = {};
        for (const nft of raw) {
          const imageURI =
            nft?.assets?.image ||
            toGateway(nft?.metadata?.image) ||
            '';

          const key = `${nft?.issuer}:${nft?.nftokenTaxon}`;

          // Build union of available attribute keys in this collection
          const attrKeys = extractAttributeKeysFromMetadata(nft?.metadata);

          (grouped[key] ||= { nfts: [], attrKeys: [] }).nfts.push({
            ...nft,
            imageURI,
            ownerUsername: nft?.ownerDetails?.username || null,
            collectionName: nft?.collectionDetails?.name || nft?.metadata?.collection?.name || null,
          });

          if (attrKeys.length) {
            const existing = new Set(grouped[key].attrKeys || []);
            for (const k of attrKeys) existing.add(k);
            grouped[key].attrKeys = Array.from(existing);
          }
        }

        setNFTs(grouped);
      } catch (e: any) {
        if (e?.name !== 'AbortError') console.error('NFT fetch error:', e);
      }
    };

    fetchNFTs();
    return () => ac.abort();
  }, [currentUserId]);

  // ====== TOKEN (trust lines) fetching & selection ======
  const [trustLines, setTrustLines] = useState<Record<string, TokenLine>>({});
  const [ruleTokenMetadata, setRuleTokenMetadata] = useState<Record<string, TokenLine>>({});
  const tokenKeys = useMemo(() => Object.keys(trustLines), [trustLines]);

  const [selectedTokenKeys, setSelectedTokenKeys] = useState<string[]>([]);
  const [perTokenMin, setPerTokenMin] = useState<Record<string, string>>({});
  const [tokenLogic, setTokenLogic] = useState<LogicOp>('OR'); // token block logic
  const [globalLogic, setGlobalLogic] = useState<LogicOp>('OR'); // between token block & nft block

  useEffect(() => {
    if (!currentUserId) return;
    const ac = new AbortController();

    const fetchTrustLines = async () => {
      try {
        const xrplAccount = currentUserId.split('@')[1]?.split(':')[0] || '';
        const client = new Client(String(API_URLS.xrplMainnetUrl));
        await client.connect();

        const response = await client.request({
          command: 'account_lines',
          account: xrplAccount,
        });
        const decodedLines = response.result.lines.map((line: any) => ({
          ...line,
          currency: line.currency,
        }));
        await client.disconnect();

        const map: Record<string, TokenLine> = {};
        
        // Fetch metadata for each token in parallel
        const tokenMetadataPromises = decodedLines
          .filter((line: any) => line.account && line.currency && line.currency !== 'XRP')
          .map(async (line: any) => {
            const issuer = line.account;
            const currency = line.currency;
            const key = `${issuer}:${currency}`;
            
            try {
              // Fetch token metadata from Bithomp
              const metadataUrl = `${API_URLS.marketPlace}/api/v2/trustlines/tokens?currency=${currency}&currencyDetails=true`;
              const metadataRes = await fetch(metadataUrl, {
                method: 'GET',
                headers: {
                  'x-bithomp-token': API_URLS.bithompToken || FALLBACK_BITHOMP,
                },
              });
              
              let tokenMetadata = null;
              if (metadataRes.ok) {
                const data = await metadataRes.json();
                // Find the matching token by issuer
                tokenMetadata = data.tokens?.find((token: any) => token.issuer === issuer);
              }

              const decodedSymbol = humanizeCurrencyCode(currency);
              const friendlyName = tokenMetadata?.name
                || tokenMetadata?.currencyDetails?.name
                || tokenMetadata?.currencyDetails?.currency
                || decodedSymbol
                || currency;
              const friendlySymbol = tokenMetadata?.currencyDetails?.currency
                || tokenMetadata?.currency
                || decodedSymbol
                || currency;
              
              map[key] = {
                issuer,
                currency,
                balance: line.balance,
                limit: line.limit,
                // Enhanced metadata from Bithomp
                icon: tokenMetadata?.icon || null,
                name: friendlyName,
                symbol: friendlySymbol,
                isLpToken: tokenMetadata?.lp_token || false,
                description: tokenMetadata?.description || null,
                stablecoin: tokenMetadata?.stablecoin || false,
                kyc: tokenMetadata?.kyc || false,
                trustlines: tokenMetadata?.trustlines || 0,
                rating: tokenMetadata?.rating || 0,
              };
            } catch (error) {
              console.error(`Error fetching metadata for ${key}:`, error);
              // Fallback to basic info
              const decodedSymbol = humanizeCurrencyCode(currency);
              map[key] = {
                issuer,
                currency,
                balance: line.balance,
                limit: line.limit,
                name: decodedSymbol || currency,
                symbol: decodedSymbol || currency,
              };
            }
          });

        await Promise.all(tokenMetadataPromises);
        setTrustLines(map);

        const mc = effectiveCondition as LockCondition;
        if (mc?.tokenLogic) setTokenLogic(mc.tokenLogic);
        if (mc?.globalLogic) setGlobalLogic(mc.globalLogic);
      } catch (e) {
        console.error('Trust lines fetch error:', e);
        setTrustLines({});
      }
    };

    fetchTrustLines();
    return () => ac.abort();
  }, [currentUserId, apiUrl, effectiveCondition]);

  useEffect(() => {
    const mc = effectiveCondition as LockCondition;
    const ruleList = Array.isArray(mc?.rules) ? mc.rules : [];
    const tokenRules = ruleList.filter(isTokenRule);

    if (!tokenRules.length) {
      setRuleTokenMetadata({});
      return;
    }

    let cancelled = false;
  const uniqueKeys = Array.from(new Set(tokenRules.map((r) => `${r.issuer}:${r.currency}`)));

    const load = async () => {
      const entries = await Promise.all(
        uniqueKeys.map(async (key) => {
          if (trustLines[key]) {
            return [key, trustLines[key]] as const;
          }

          const [issuer, currency = ''] = key.split(':');
          const meta = await fetchTokenMetadata(issuer, currency);
          return [key, meta] as const;
        })
      );

      if (!cancelled) {
        setRuleTokenMetadata(Object.fromEntries(entries));
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [effectiveCondition, trustLines]);

  // ====== Selection handlers ======
  const hasAnyNFTSelection = useMemo(() => selectedKeys.length > 0, [selectedKeys]);
  const hasAnyTokenSelection = useMemo(() => selectedTokenKeys.length > 0, [selectedTokenKeys]);

  const onToggleKey = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      if (prev.includes(key)) {
        setPerCollectionCounts((c) => {
          const { [key]: _omit, ...rest } = c;
          return rest;
        });
        setAttrPairs((s) => {
          const { [key]: _o, ...r } = s; return r;
        });
        setAttrLogic((s) => {
          const { [key]: _o, ...r } = s; return r;
        });
        return prev.filter((k) => k !== key);
      }
      setPerCollectionCounts((c) => ({ ...c, [key]: c[key] ?? 1 }));
      // start with empty attribute rows + default logic AND
      setAttrPairs((s) => ({ ...s, [key]: s[key] ?? [] }));
      setAttrLogic((s) => ({ ...s, [key]: s[key] ?? 'AND' }));
      return [...prev, key];
    });
  }, []);

  // ====== Manual collection fetching ======
  const fetchManualCollection = useCallback(async (issuer: string, taxon: string) => {
    if (!issuer || !taxon) {
      setManualCollectionError('Please provide both Issuer and Taxon');
      return;
    }

    setIsLoadingManualCollection(true);
    setManualCollectionError('');

    try {
      // First try to get collection details
      const collectionUrl = `${API_URLS.marketPlace}/api/v2/nfts?issuer=${issuer}&taxon=${taxon}&assets=true&collectionDetails=true&limit=1`;
      
      const collectionRes = await fetch(collectionUrl, {
        method: 'GET',
        headers: {
          'x-bithomp-token': API_URLS.bithompToken || FALLBACK_BITHOMP,
        },
      });

      if (!collectionRes.ok) {
        throw new Error(`Failed to fetch collection info: ${collectionRes.status}`);
      }

      const collectionData = await collectionRes.json();
      const nfts = collectionData.nfts || [];

      if (nfts.length === 0) {
        throw new Error('No NFTs found for this Issuer/Taxon combination');
      }

      const sampleNft = nfts[0];
      const key = `${issuer}:${taxon}`;
      
      // Check if this collection already exists in owned collections
      if (NFTs[key]) {
        setManualCollectionError('This collection is already in your owned collections');
        return;
      }

      // Check if this manual collection already exists
      if (manualNFTs[key]) {
        setManualCollectionError('This collection has already been added manually');
        return;
      }

      const imageURI = sampleNft?.assets?.image || toGateway(sampleNft?.metadata?.image) || '';
      const attrKeys = extractAttributeKeysFromMetadata(sampleNft?.metadata);

      // Create a synthetic NFT entry for manual collections
      const manualNftEntry = {
        ...sampleNft,
        imageURI,
        collectionName: sampleNft?.collectionDetails?.name || sampleNft?.metadata?.collection?.name || `Collection ${taxon}`,
        isManualEntry: true,
      };

      setManualNFTs(prev => ({
        ...prev,
        [key]: {
          nfts: [manualNftEntry],
          attrKeys: attrKeys,
        }
      }));

      // Clear the form
      setManualIssuer('');
      setManualTaxon('');
      setShowManualInput(false);
      
    } catch (error: any) {
      console.error('Manual collection fetch error:', error);
      setManualCollectionError(error.message || 'Failed to fetch collection information');
    } finally {
      setIsLoadingManualCollection(false);
    }
  }, [NFTs, manualNFTs]);

  const removeManualCollection = useCallback((key: string) => {
    setManualNFTs(prev => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
    
    // Also remove from selection if selected
    setSelectedKeys(prev => prev.filter(k => k !== key));
    setPerCollectionCounts(prev => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
    setAttrPairs(prev => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
    setAttrLogic(prev => {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const setCountForKey = useCallback((key: string, value: number) => {
    setPerCollectionCounts((prev) => ({ ...prev, [key]: Math.max(1, value || 1) }));
  }, []);

  const toggleTokenKey = useCallback((key: string) => {
    setSelectedTokenKeys(prev => {
      if (prev.includes(key)) {
        setPerTokenMin(({ [key]: _omit, ...rest }) => rest);
        return prev.filter(k => k !== key);
      }
      setPerTokenMin(c => ({ ...c, [key]: c[key] ?? '1' }));
      return [...prev, key];
    });
  }, []);

  const setMinForTokenKey = useCallback((key: string, value: string) => {
    setPerTokenMin(prev => ({ ...prev, [key]: value && Number(value) > 0 ? value : '1' }));
  }, []);

  const nextStep = useCallback(() => setActiveStep((s) => s + 1), []);
  const prevStep = useCallback(() => setActiveStep((s) => s - 1), []);
  const skipStep = useCallback(() => setActiveStep((s) => s + 2), []);
  const onKickChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setKickMessage(e.target.value),
    [setKickMessage]
  );

  // ====== Prepopulate from saved rules (first time NFTs/Tokens arrive) ======
  useEffect(() => {
    if (prepopulated || (!collectionKeys.length && !tokenKeys.length)) return;

    const mc = effectiveCondition as LockCondition;
    const rules = Array.isArray(mc?.rules) ? mc.rules : [];

    if (rules.length) {
      // NFT rules
      const savedNftKeys: string[] = [];
      const counts: Record<string, number> = {};
      const pairsMap: Record<string, AttrRow[]> = {};
      const logicMap: Record<string, LogicOp> = {};

      for (const r of rules) {
        const isNFT = (r as any).kind ? (r as any).kind === 'NFT' : (r as any).taxon != null;
        if (isNFT) {
          const rn = r as any as NFTSubRule & {
            attributeKey?: string; attributeValue?: string;
            attributeFilters?: { key: string; value: string }[];
            attributeLogic?: LogicOp;
          };
          const key = `${(rn as any).issuer}:${(rn as any).taxon}`;
          
          // Check if collection exists in either owned or manual NFTs
          if (allNFTs[key]) {
            savedNftKeys.push(key);
            counts[key] = (rn as any).nftCount || 1;

            // Load multi-attributes if present
            if (Array.isArray((rn as any).attributeFilters) && (rn as any).attributeFilters.length) {
              pairsMap[key] = (rn as any).attributeFilters.map((f: any, idx: any) => ({
                id: `${key}-${idx}`,
                key: f.key ?? '',
                value: (f as any).value ?? '',
              }));
            } else if ((rn as any).attributeKey) {
              // Back-compat: single attribute
              pairsMap[key] = [{
                id: `${key}-0`,
                key: (rn as any).attributeKey!,
                value: ((rn as any).attributeValue ?? '') as string,
              }];
            }

            logicMap[key] = (rn as any).attributeLogic || 'AND';
          }
        }
      }
      if (savedNftKeys.length) {
        setSelectedKeys(savedNftKeys);
        setPerCollectionCounts(counts);
      }
      if (Object.keys(pairsMap).length) setAttrPairs(pairsMap);
      if (Object.keys(logicMap).length) setAttrLogic(logicMap);

      // Token rules
      const savedTokenKeys: string[] = [];
      const mins: Record<string, string> = {};
      for (const r of rules) {
        const isTOKEN = (r as any).kind ? (r as any).kind === 'TOKEN' : (r as any).currency != null && (r as any).taxon == null;
        if (isTOKEN) {
          const rt = r as TokenSubRule;
          const key = `${rt.issuer}:${rt.currency}`;
          if (trustLines[key]) {
            savedTokenKeys.push(key);
            mins[key] = (rt as any).minAmount || '1';
          }
        }
      }
      if (savedTokenKeys.length) {
        setSelectedTokenKeys(savedTokenKeys);
        setPerTokenMin(mins);
      }
    }

    // logic switches
    if ((effectiveCondition as any)?.nftLogic) setLogicOp((effectiveCondition as any).nftLogic);
    if ((effectiveCondition as any)?.tokenLogic) setTokenLogic((effectiveCondition as any).tokenLogic);
    if ((effectiveCondition as any)?.globalLogic) setGlobalLogic((effectiveCondition as any).globalLogic);

    setPrepopulated(true);
  }, [allNFTs, trustLines, collectionKeys.length, tokenKeys.length, prepopulated, effectiveCondition]);

  // ====== Save (merge NFT + Token rules and knobs) ======
  const onSave = useCallback(async () => {
    // Build rules from current selections (allow token-only or nft-only)
    const nftRules: NFTSubRule[] = selectedKeys.map((key) => {
      const nft = allNFTs[key]?.nfts?.[0];
      const rule: any = {
        kind: 'NFT',
        issuer: nft.issuer,
        taxon: nft.nftokenTaxon,
        nftCount: perCollectionCounts[key] || 1,
        nftImageUrl: nft.imageURI || null,
        collectionName: nft.collectionName || nft.metadata?.name || null,
      };

      const pairs = (attrPairs[key] || []).filter(p => p.key && (p.value ?? '') !== '');
      if (pairs.length) {
        // new multi-attribute shape
        rule.attributeFilters = pairs.map(p => ({ key: p.key, value: p.value }));
        // NEW: attribute AND/OR logic per collection
        rule.attributeLogic = attrLogic[key] || 'AND';

        // optional: legacy single fields for back-compat
        if (!rule.attributeKey) {
          rule.attributeKey = pairs[0].key;
          rule.attributeValue = pairs[0].value;
        }
      }
      return rule as NFTSubRule;
    });

    const tokenRules: TokenSubRule[] = selectedTokenKeys.map((key) => {
      const tl = trustLines[key];
      return {
        kind: 'TOKEN',
        issuer: tl.issuer,
        currency: tl.currency,
        minAmount: (perTokenMin[key] ?? '1').toString(),
        tokenName: tl.name || tl.symbol || humanizeCurrencyCode(tl.currency),
        tokenSymbol: tl.symbol || humanizeCurrencyCode(tl.currency),
        tokenIcon: tl.icon || null,
      };
    });

    const first = nftRules[0];

    const updated: LockCondition = {
      ...(effectiveCondition as LockCondition),
      id: effectiveCondition?.id || `lock-${Date.now()}`,
      type: 'lock',
      globalLogic,
      tokenLogic,
      nftLogic: logicOp,
      rules: [...nftRules, ...tokenRules],
      // legacy preview fields
      issuer: first?.issuer || '',
      taxon: (first?.taxon as any)?.toString?.() || '',
      nftCount: first?.nftCount || 1,
      nftImageUrl: first?.nftImageUrl || null,
      collectionName: first?.collectionName || null,
    };

    setSaveRequestResult(1);
    setEditingBasic(updated);
    
    // Wait for the save to complete before setting success state
    try {
      await saveConditionTree(updated);
      setActiveStep(0);
    } catch (error) {
      console.error('Save failed in onSave:', error);
      setSaveRequestResult(0); // Reset to idle state on error
    }
  }, [
    allNFTs,
    effectiveCondition,
    logicOp,
    globalLogic,
    tokenLogic,
    perCollectionCounts,
    perTokenMin,
    selectedKeys,
    selectedTokenKeys,
    trustLines,
    saveConditionTree,
    attrPairs,
    attrLogic,
  ]);

  // ====== Confirmation dialog handlers ======
  const handleSaveClick = useCallback(() => {
    setShowConfirmDialog(true);
  }, []);

  const handleConfirmSave = useCallback(async () => {
    setShowConfirmDialog(false);
    try {
      await onSave();
    } catch (error) {
      console.error('Confirm save failed:', error);
    }
  }, [onSave]);

  const handleCancelSave = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  // ====== Derived validation for Save ======
  const nftBlockConfigured = hasAnyNFTSelection;
  const tokenBlockConfigured = hasAnyTokenSelection;

  const canSave: boolean = useMemo(() => {
    // If Global=AND → require both blocks to have at least one rule
    // If Global=OR  → require at least one block to have rules
    return globalLogic === 'AND'
      ? (nftBlockConfigured && tokenBlockConfigured)
      : (nftBlockConfigured || tokenBlockConfigured);
  }, [globalLogic, nftBlockConfigured, tokenBlockConfigured]);

  const saveDisabledReason = useMemo(() => {
    if (canSave) return '';
    if (globalLogic === 'AND') return 'AND logic requires at least one NFT rule and one Token rule.';
    return 'Add at least one NFT or one Token rule.';
  }, [canSave, globalLogic]);

  // ====== UI gating for power levels ======
  if (checkedPowerLevels === 0) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-white dark:bg-[#363C43] z-50">
        <Loader2 className="animate-spin text-blue-600 dark:text-blue-400 w-12 h-12 mb-4" />
        <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
          Please wait while we check your permissions...
        </p>
      </div>
    );
  }

  if (checkedPowerLevels === 1) {
    return <RoomRulesViewer />;
  }

  // Single tab (kept for extensibility)
  const currentTab = 'nft';

  // helpers for dynamic attribute rows
  const addAttrRow = (key: string) => {
    setAttrPairs((s) => {
      const rows = s[key] || [];
      return {
        ...s,
        [key]: [...rows, { id: `${key}-${Date.now()}-${Math.random()}`, key: '', value: '' }],
      };
    });
    setAttrLogic((s) => ({ ...s, [key]: s[key] ?? 'AND' }));
  };

  const removeAttrRow = (key: string, id: string) => {
    setAttrPairs((s) => ({
      ...s,
      [key]: (s[key] || []).filter(r => r.id !== id),
    }));
  };

  const changeAttrKey = (key: string, id: string, newKey: string) => {
    setAttrPairs((s) => ({
      ...s,
      [key]: (s[key] || []).map(r => (r.id === id ? { ...r, key: newKey } : r)),
    }));
  };

  const changeAttrValue = (key: string, id: string, val: string) => {
    setAttrPairs((s) => ({
      ...s,
      [key]: (s[key] || []).map(r => (r.id === id ? { ...r, value: val } : r)),
    }));
  };

  const currentConfigSection = (() => {
    const mc = previewCondition as LockCondition;
    const rules = Array.isArray(mc.rules) ? mc.rules : [];

    const statusLabel = isGateActive ? 'Active' : 'Inactive';
    const borderColor = isGateActive ? 'success.200' : 'warning.200';
    const backgroundColor = isGateActive ? 'success.50' : 'warning.50';
    const headerColor = isGateActive ? 'success.main' : 'warning.main';

    if (rules.length) {
      return (
        <Box
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            border: 2,
            borderColor,
            borderRadius: 3,
            bgcolor: backgroundColor,
            boxShadow: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="h6" gutterBottom color={headerColor} fontWeight={700} sx={{ mb: 0 }}>
              {`Current Token Gate (${statusLabel})`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isGateActive}
                    onChange={handleGateToggle}
                    color="primary"
                    disabled={isUpdatingGateActive}
                  />
                }
                label={isGateActive ? 'Enabled' : 'Disabled'}
                labelPlacement="start"
                sx={{
                  m: 0,
                  '& .MuiTypography-root': {
                    fontWeight: 600,
                    color: isGateActive ? 'text.primary' : 'text.secondary',
                  },
                }}
              />
              {isUpdatingGateActive && <CircularProgress size={20} />}
            </Box>
          </Box>

          {activeToggleError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {activeToggleError}
            </Alert>
          )}

          {!isGateActive && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Token gate enforcement is currently <strong>disabled</strong>. Anyone can join this room until you re-enable the rule.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {mc.globalLogic === 'AND'
              ? 'Users must satisfy BOTH the Token block and the NFT block (AND).'
              : 'Users may satisfy EITHER the Token block or the NFT block (OR).'}
          </Typography>

          <Stack spacing={1}>
            {rules.map((r: any, idx: number) => {
              const isNFT = r.kind ? r.kind === 'NFT' : r.taxon != null;
              if (isNFT) {
                const filters = Array.isArray(r.attributeFilters) ? r.attributeFilters : [];
                return (
                  <Stack key={`NFT-${r.issuer}:${r.taxon}:${idx}`} direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                    {r.nftImageUrl ? (
                      <Avatar
                        variant="rounded"
                        src={r.nftImageUrl}
                        sx={{ width: 40, height: 40, border: 2, borderColor: headerColor }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: 'grey.200',
                          borderRadius: 2,
                          border: 2,
                          borderColor: 'grey.400',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          NFT
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {r.collectionName || 'Collection'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={500}
                        noWrap
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        Issuer: {r.issuer} • Taxon: {r.taxon}
                      </Typography>
                      {filters.length > 0 && (
                        <Box sx={{ mt: 0.25 }}>
                          {filters.map((f: any, i: number) => (
                            <Typography key={`${idx}-f-${i}`} variant="caption" color="primary.main" sx={{ display: 'block' }}>
                              Dynamic: {f.key} = <b>{String(f.value)}</b>
                            </Typography>
                          ))}
                          {filters.length > 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Attribute logic: <b>{r.attributeLogic || 'AND'}</b>
                            </Typography>
                          )}
                        </Box>
                      )}
                      {!filters.length && r.attributeKey && (r.attributeValue ?? '') !== '' && (
                        <Typography variant="caption" color="primary.main" sx={{ display: 'block' }}>
                          Dynamic: {r.attributeKey} = <b>{String(r.attributeValue)}</b>
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" fontWeight={700}>≥ {r.nftCount}</Typography>
                  </Stack>
                );
              }

              const tokenKey = `${r.issuer}:${r.currency}`;
              const tokenMeta = ruleTokenMetadata[tokenKey] || trustLines[tokenKey];
              const tokenIcon = r.tokenIcon || tokenMeta?.icon || null;
              const tokenLabel = r.tokenName || tokenMeta?.name || humanizeCurrencyCode(r.currency);
              const tokenSymbol = r.tokenSymbol || tokenMeta?.symbol || humanizeCurrencyCode(r.currency);

              return (
                <Stack
                  key={`TOK-${r.issuer}:${r.currency}:${idx}`}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ flexWrap: 'wrap' }}
                >
                  {tokenIcon ? (
                    <Avatar
                      src={tokenIcon}
                      variant="rounded"
                      sx={{ width: 40, height: 40, border: 1, borderColor: headerColor }}
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: 'grey.200',
                        color: 'text.secondary',
                        fontWeight: 700,
                        border: 1,
                        borderColor: 'grey.400',
                      }}
                    >
                      {String(tokenSymbol || tokenLabel || r.currency).slice(0, 4)}
                    </Avatar>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>
                      {tokenLabel}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                      sx={{ display: 'block', wordBreak: 'break-all' }}
                    >
                      Issuer: {r.issuer}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                      sx={{ display: 'block' }}
                      title={r.currency}
                    >
                      Currency Code: {tokenSymbol || tokenLabel}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700}>≥ {r.minAmount}</Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      );
    }

    if (loadedIssuer || loadedTaxon) {
      return (
        <Box
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            border: 2,
            borderColor,
            borderRadius: 3,
            bgcolor: backgroundColor,
            boxShadow: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="h6" gutterBottom color={headerColor} fontWeight={700} sx={{ mb: 0 }}>
              {`Current Token Gate (${statusLabel})`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isGateActive}
                    onChange={handleGateToggle}
                    color="primary"
                    disabled={isUpdatingGateActive}
                  />
                }
                label={isGateActive ? 'Enabled' : 'Disabled'}
                labelPlacement="start"
                sx={{
                  m: 0,
                  '& .MuiTypography-root': {
                    fontWeight: 600,
                    color: isGateActive ? 'text.primary' : 'text.secondary',
                  },
                }}
              />
              {isUpdatingGateActive && <CircularProgress size={20} />}
            </Box>
          </Box>

          {activeToggleError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {activeToggleError}
            </Alert>
          )}

          {!isGateActive && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Token gate enforcement is currently <strong>disabled</strong>. Anyone can join this room until you re-enable the rule.
            </Alert>
          )}
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              {loadedNftImageUrl ? (
                <Avatar variant="rounded" src={loadedNftImageUrl} sx={{ width: 64, height: 64, border: 2, borderColor: headerColor, boxShadow: 2 }} />
              ) : (
                <Box sx={{ width: 64, height: 64, bgcolor: 'grey.200', borderRadius: 2, border: 2, borderColor: 'grey.400', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>No Image</Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs>
              <Typography variant="body1" fontWeight={700} color={headerColor}>
                {loadedCollectionName || 'Collection'}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>Issuer: {loadedIssuer}</Typography>
              <br />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Taxon: {loadedTaxon} • Minimum: {loadedNftCount} NFT{loadedNftCount > 1 ? 's' : ''}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      );
    }

    return null;
  })();

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Token Gate Settings {displayName ? `for ${displayName}` : ''}
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom sx={{ maxWidth: 600, textAlign: 'center' }}>
            Limit room access to users who own specific NFTs (with optional dynamic attributes) or hold required amounts of XRPL tokens
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {savedMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Settings saved successfully!
          </Alert>
        )}
        {botJoinError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              🤖 Bot Invitation Required
            </Typography>
            <Typography variant="body2">
              {botJoinError}
            </Typography>
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={currentTab}
            variant="fullWidth"
            aria-label="Token Gate settings tabs"
            sx={{
              '& .MuiTab-root': { fontWeight: 600, fontSize: '0.95rem', textTransform: 'none', py: 1.5 },
              '& .Mui-selected': { color: 'primary.main' },
            }}
          >
            <Tab label="Access Rules" value="nft" />
          </Tabs>
        </Box>

        {/* If condition tree still loading, show a friendly loader (no early returns) */}
        {!savedConditionTree ? (
          <Box sx={{ py: 6, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} />
            <Typography>Loading current configuration…</Typography>
          </Box>
        ) : (
          <Box sx={{ py: 3 }}>
            {currentConfigSection}
            <Typography variant="h5" fontWeight="bold" textAlign="center" mb={3} color="primary.main">
              Configure Token Gate
            </Typography>

            <Stepper
              activeStep={activeStep}
              orientation="vertical"
              sx={{
                '& .MuiStepLabel-label': { fontSize: '1.05rem', fontWeight: 600 },
                '& .MuiStepLabel-label.Mui-active': { color: 'primary.main' },
                '& .MuiStepLabel-label.Mui-completed': { color: 'success.main' },
                '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
                '& .MuiStepIcon-root.Mui-completed': { color: 'success.main' },
              }}
            >
              {/* Step 1: NFT collections */}
              <Step>
                <StepLabel>Choose NFT Collections</StepLabel>
                <StepContent>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Select one or more NFT collections that members must own. You can also <b>skip</b> this step if you only want token rules.
                    </Typography>

                    {/* Manual Collection Input Section */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                          Add Collection by Issuer/Taxon
                        </Typography>
                        <Button
                          startIcon={showManualInput ? <CloseIcon /> : <AddIcon />}
                          onClick={() => {
                            setShowManualInput(!showManualInput);
                            setManualCollectionError('');
                            setManualIssuer('');
                            setManualTaxon('');
                          }}
                          size="small"
                          sx={{ 
                            borderRadius: 2, 
                            textTransform: 'none',
                            fontWeight: 600,
                            color: showManualInput ? 'text.secondary' : 'primary.main'
                          }}
                        >
                          {showManualInput ? 'Cancel' : 'Add Collection'}
                        </Button>
                      </Box>

                      {showManualInput && (
                        <Card sx={{ p: 2, border: 1, borderColor: 'primary.200', borderRadius: 2, bgcolor: 'primary.25' }}>
                          <Typography variant="body2" color="text.secondary" mb={2}>
                            Enter the Issuer address and Taxon of an NFT collection to add it to your gate options.
                          </Typography>
                          
                          <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} sm={5}>
                              <TextField
                                label="Issuer Address"
                                placeholder="rXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                                value={manualIssuer}
                                onChange={(e) => setManualIssuer(e.target.value.trim())}
                                fullWidth
                                size="small"
                                disabled={isLoadingManualCollection}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                helperText="XRPL address that issued the NFT collection"
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                label="Taxon"
                                placeholder="0"
                                type="number"
                                value={manualTaxon}
                                onChange={(e) => setManualTaxon(e.target.value.trim())}
                                fullWidth
                                size="small"
                                disabled={isLoadingManualCollection}
                                InputProps={{ inputProps: { min: 0 } }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                helperText="NFT taxon number"
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Button
                                variant="contained"
                                onClick={() => fetchManualCollection(manualIssuer, manualTaxon)}
                                disabled={!manualIssuer || !manualTaxon || isLoadingManualCollection}
                                startIcon={isLoadingManualCollection ? <CircularProgress size={16} /> : <AddIcon />}
                                sx={{ 
                                  borderRadius: 2, 
                                  textTransform: 'none', 
                                  fontWeight: 600,
                                  width: '100%'
                                }}
                              >
                                {isLoadingManualCollection ? 'Adding...' : 'Add Collection'}
                              </Button>
                            </Grid>
                          </Grid>

                          {manualCollectionError && (
                            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                              {manualCollectionError}
                            </Alert>
                          )}
                        </Card>
                      )}
                    </Box>

                    {collectionKeys.length > 0 ? (
                      <Box>
                        {/* Search Bar */}
                        <TextField
                          placeholder="Search collections by name, issuer, or taxon..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          fullWidth
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover': { bgcolor: 'background.paper' },
                              '&.Mui-focused': { bgcolor: 'background.paper' },
                            },
                          }}
                        />

                        {/* Collections Grid */}
                        <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 2 }}>
                          <Grid container spacing={2}>
                            {collectionKeys
                              .filter((key) => {
                                if (!searchQuery.trim()) return true;
                                const nft = allNFTs[key]?.nfts?.[0];
                                if (!nft) return false;
                                const q = searchQuery.toLowerCase();
                                const name = (nft.collectionName || nft.metadata?.name || '').toLowerCase();
                                const issuer = (nft.issuer || '').toLowerCase();
                                const taxon = String(nft.nftokenTaxon || '').toLowerCase();
                                return name.includes(q) || issuer.includes(q) || taxon.includes(q);
                              })
                              .map((key) => {
                                const nft = allNFTs[key].nfts?.[0];
                                if (!nft) return null;
                                const isSelected = selectedKeys.includes(key);
                                const isManual = nft.isManualEntry;
                                const isOwned = NFTs[key] !== undefined;

                                return (
                                  <Grid item xs={6} sm={4} md={3} key={key}>
                                    <Card
                                      onClick={() => onToggleKey(key)}
                                      sx={{
                                        cursor: 'pointer',
                                        border: isSelected ? 2 : 1,
                                        borderColor: isSelected ? 'primary.main' : (isManual ? 'warning.main' : 'divider'),
                                        bgcolor: isSelected ? 'primary.50' : (isManual ? 'warning.25' : 'background.paper'),
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                          borderColor: 'primary.main',
                                          transform: 'translateY(-2px)',
                                          boxShadow: 2,
                                        },
                                        position: 'relative',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      {/* Collection Type Badge */}
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 8,
                                          left: 8,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          bgcolor: isOwned ? 'success.main' : 'warning.main',
                                          color: 'white',
                                          px: 1,
                                          py: 0.3,
                                          borderRadius: 999,
                                          boxShadow: 1,
                                          zIndex: 1,
                                        }}
                                      >
                                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>
                                          {isOwned ? 'OWNED' : 'MANUAL'}
                                        </Typography>
                                      </Box>

                                      {/* Selected Badge */}
                                      {isSelected && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText',
                                            px: 1,
                                            py: 0.3,
                                            borderRadius: 999,
                                            boxShadow: 1,
                                            zIndex: 1,
                                          }}
                                        >
                                          <CheckCircleIcon sx={{ fontSize: 16 }} />
                                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                            Selected
                                          </Typography>
                                        </Box>
                                      )}

                                      {/* Manual Collection Remove Button */}
                                      {isManual && (
                                        <IconButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeManualCollection(key);
                                          }}
                                          sx={{
                                            position: 'absolute',
                                            top: 40,
                                            right: 8,
                                            bgcolor: 'error.main',
                                            color: 'white',
                                            width: 24,
                                            height: 24,
                                            zIndex: 1,
                                            '&:hover': { bgcolor: 'error.dark' }
                                          }}
                                          size="small"
                                        >
                                          <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      )}

                                      <CardMedia
                                        component="img"
                                        height="120"
                                        image={nft.imageURI || '/placeholder-nft.png'}
                                        alt={nft.metadata?.name || 'NFT'}
                                        sx={{ objectFit: 'cover', bgcolor: 'grey.100' }}
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.src = '/placeholder-nft.png';
                                        }}
                                      />

                                      <Box sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Typography
                                          variant="body2"
                                          fontWeight="600"
                                          sx={{
                                            mb: 0.5,
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {nft.collectionName || nft.metadata?.name || 'Unnamed Collection'}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            fontSize: '0.75rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            mt: 'auto',
                                          }}
                                        >
                                          Taxon: {nft.nftokenTaxon}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            fontSize: '0.7rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {isManual ? 'Manual Entry' : `${allNFTs[key].nfts.length} NFT${allNFTs[key].nfts.length > 1 ? 's' : ''}`}
                                        </Typography>
                                      </Box>
                                    </Card>
                                  </Grid>
                                );
                              })}
                          </Grid>
                        </Box>

                        {hasAnyNFTSelection && (
                          <Alert severity="success" sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { fontWeight: 500 } }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {selectedKeys.map((key) => {
                                const nft = allNFTs[key].nfts[0];
                                const isManual = nft.isManualEntry;
                                return (
                                  <Chip
                                    key={key}
                                    label={`${nft.collectionName || nft.metadata?.name || key} ${isManual ? '(Manual)' : ''}`}
                                    onDelete={() => onToggleKey(key)}
                                    variant="filled"
                                    color="success"
                                    sx={{ borderRadius: 999 }}
                                  />
                                );
                              })}
                            </Stack>
                          </Alert>
                        )}
                      </Box>
                    ) : (
                      <Alert
                        severity="info"
                        sx={{
                          borderRadius: 2,
                          bgcolor: 'info.50',
                          '& .MuiAlert-message': { fontWeight: 500 },
                        }}
                      >
                        No NFT collections found on your address. You can continue to set <b>Token</b> rules.
                      </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        onClick={nextStep}
                        endIcon={<NavigateNextIcon />}
                        sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600, textTransform: 'none', boxShadow: 2, '&:hover': { boxShadow: 4 } }}
                      >
                        Continue
                      </Button>
                      {!hasAnyNFTSelection && (
                        <Button
                          onClick={skipStep}
                          startIcon={<NavigateNextIcon />}
                          sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', color: 'text.secondary', '&:hover': { bgcolor: 'grey.100' } }}
                        >
                          Skip NFTs
                        </Button>
                      )}
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 2: NFT requirements & logic + Dynamic attributes (MULTI + AND/OR) */}
              <Step>
                <StepLabel>Set NFT Requirements & Dynamic Attributes</StepLabel>
                <StepContent>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Specify counts per selected collection. Optionally add one or more <b>Dynamic NFT</b> attribute rules:
                      <br />• Select an attribute from the dropdown &nbsp;• Enter the expected value &nbsp;• Choose AND/OR when 2+ rows
                    </Typography>

                    {hasAnyNFTSelection && (
                      <Stack spacing={2} sx={{ mb: 3 }}>
                        {selectedKeys.map((key) => {
                          const nft = allNFTs[key].nfts[0];
                          const attrs = allNFTs[key].attrKeys || [];
                          const hasAttrs = attrs.length > 0;
                          const rows = attrPairs[key] || [];
                          const thisAttrLogic = attrLogic[key] || 'AND';
                          const isManual = nft.isManualEntry;

                          // if a saved/legacy key isn't in attrs anymore, still show it as a selectable option
                          const ensureMenuKeys = (k: string) =>
                            (attrs.includes(k) ? attrs : [...attrs, k]).filter(Boolean);

                          return (
                            <Card
                              key={key}
                              sx={{ 
                                p: 2, 
                                bgcolor: isManual ? 'warning.50' : 'primary.50', 
                                border: 1, 
                                borderColor: isManual ? 'warning.200' : 'primary.200', 
                                borderRadius: 2, 
                                boxShadow: 1 
                              }}
                            >
                              <Grid container spacing={2}>
                                <Grid item xs={12} md="auto">
                                  <Box sx={{ position: 'relative' }}>
                                    <Avatar 
                                      variant="rounded" 
                                      src={nft.imageURI} 
                                      sx={{ 
                                        width: 48, 
                                        height: 48, 
                                        border: 2, 
                                        borderColor: isManual ? 'warning.main' : 'primary.main' 
                                      }} 
                                    />
                                    {isManual && (
                                      <Chip
                                        label="Manual"
                                        size="small"
                                        sx={{
                                          position: 'absolute',
                                          top: -8,
                                          right: -8,
                                          bgcolor: 'warning.main',
                                          color: 'white',
                                          fontSize: '0.6rem',
                                          height: 16,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Grid>

                                <Grid item xs md>
                                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                                    {nft.collectionName || nft.metadata?.name || 'Unnamed Collection'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                    Issuer: {nft.issuer}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                    Taxon: {nft.nftokenTaxon}
                                  </Typography>

                                  <Grid container spacing={2} sx={{ mt: 1 }}>
                                    {/* Minimum count (original rule) */}
                                    <Grid item xs={12} sm={6} md={4}>
                                      <TextField
                                        label="Minimum NFT Count"
                                        type="number"
                                        fullWidth
                                        value={perCollectionCounts[key] ?? 1}
                                        onChange={(e) => setCountForKey(key, parseInt(e.target.value, 10))}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <FormatListNumberedIcon color="primary" />
                                            </InputAdornment>
                                          ),
                                          inputProps: { min: 1 },
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        helperText="Required from this collection"
                                      />
                                    </Grid>

                                    {/* Dynamic attributes: MULTI rows */}
                                    <Grid item xs={12}>
                                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Dynamic attributes (optional)
                                      </Typography>

                                      {!hasAttrs && (
                                        <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
                                          This collection’s metadata has no attributes. You can still use the original NFT count rule.
                                        </Alert>
                                      )}

                                      {rows.map((row) => {
                                        const menuKeys = ensureMenuKeys(row.key);
                                        return (
                                          <Grid key={row.id} container spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                                            <Grid item xs={12} sm={5} md={4}>
                                              <FormControl fullWidth disabled={!hasAttrs}>
                                                <InputLabel id={`attr-key-${key}-${row.id}`}>Attribute</InputLabel>
                                                <Select
                                                  labelId={`attr-key-${key}-${row.id}`}
                                                  label="Attribute"
                                                  value={row.key}
                                                  onChange={(e) => changeAttrKey(key, row.id, String(e.target.value))}
                                                  sx={{ borderRadius: 2 }}
                                                >
                                                  {menuKeys.map((a) => (
                                                    <MenuItem key={a} value={a}>{a}</MenuItem>
                                                  ))}
                                                </Select>
                                                <FormHelperText>
                                                  {hasAttrs ? 'Select attribute key' : 'No attributes available'}
                                                </FormHelperText>
                                              </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={5} md={4}>
                                              <TextField
                                                label="Expected value"
                                                fullWidth
                                                disabled={!hasAttrs}
                                                value={row.value}
                                                onChange={(e) => changeAttrValue(key, row.id, e.target.value)}
                                                placeholder="e.g., true / gold / 10"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                helperText="String/number/true/false"
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={2} md="auto">
                                              <IconButton
                                                aria-label="Remove attribute"
                                                onClick={() => removeAttrRow(key, row.id)}
                                                disabled={!hasAttrs}
                                              >
                                                <DeleteOutlineIcon />
                                              </IconButton>
                                            </Grid>
                                          </Grid>
                                        );
                                      })}

                                      <Button
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={() => addAttrRow(key)}
                                        disabled={!hasAttrs}
                                        sx={{ mt: rows.length ? 0.5 : 0 }}
                                      >
                                        Add attribute
                                      </Button>

                                      {/* NEW: Attribute-level AND/OR when there are 2+ rows */}
                                      {hasAttrs && rows.length >= 2 && (
                                        <Box sx={{ mt: 2 }}>
                                          <Typography variant="subtitle2" color="primary.main" fontWeight={600} mb={1}>
                                            Attribute Logic
                                          </Typography>
                                          <ToggleButtonGroup
                                            exclusive
                                            value={thisAttrLogic}
                                            onChange={(_, val) => val && setAttrLogic((s) => ({ ...s, [key]: val }))}
                                            sx={{ '& .MuiToggleButton-root': { borderRadius: 2, textTransform: 'none', px: 2.5, py: 1, fontWeight: 600 } }}
                                          >
                                            <ToggleButton value="AND">Require ALL attributes (AND)</ToggleButton>
                                            <ToggleButton value="OR">Allow ANY attribute (OR)</ToggleButton>
                                          </ToggleButtonGroup>
                                        </Box>
                                      )}
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Card>
                          );
                        })}
                      </Stack>
                    )}

                    {selectedKeys.length > 1 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="primary.main" fontWeight={600} mb={1}>
                          NFT Logic
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          value={logicOp}
                          onChange={(_, val) => val && setLogicOp(val)}
                          sx={{ '& .MuiToggleButton-root': { borderRadius: 2, textTransform: 'none', px: 2.5, py: 1, fontWeight: 600 } }}
                        >
                          <ToggleButton value="AND">Users must meet ALL NFT requirements (AND)</ToggleButton>
                          <ToggleButton value="OR">Users may meet ANY one NFT requirement (OR)</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button variant="contained" onClick={nextStep} endIcon={<NavigateNextIcon />} sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600 }}>
                        Continue
                      </Button>
                      {!hasAnyNFTSelection && (
                        <Button onClick={nextStep} startIcon={<NavigateNextIcon />} sx={{ borderRadius: 2, px: 3, py: 1, color: 'text.secondary' }}>
                          Skip
                        </Button>
                      )}
                      <Button onClick={prevStep} startIcon={<NavigateBeforeIcon />} sx={{ borderRadius: 2, px: 3, py: 1, color: 'text.secondary' }}>
                        Back
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: Choose Tokens */}
              <Step>
                <StepLabel>Choose Tokens</StepLabel>
                <StepContent>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Select fungible tokens (from your trust lines). You can <b>skip</b> this if you only want NFT rules.
                    </Typography>

                    {tokenKeys.length > 0 ? (
                      <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                        <Grid container spacing={2}>
                          {tokenKeys.map((key) => {
                            const tl = trustLines[key];
                            const isSelected = selectedTokenKeys.includes(key);
                            return (
                              <Grid item xs={12} sm={6} md={4} key={key}>
                                <Card
                                  onClick={() => toggleTokenKey(key)}
                                  sx={{
                                    cursor: 'pointer',
                                    border: isSelected ? 2 : 1,
                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                    bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                    transition: 'all 0.2s',
                                    '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                                    position: 'relative',
                                    height: '100%',
                                  }}
                                >
                                  {/* LP Token Badge */}
                                  {tl.isLpToken && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'info.main',
                                        color: 'white',
                                        px: 1,
                                        py: 0.3,
                                        borderRadius: 999,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        zIndex: 1,
                                      }}
                                    >
                                      LP
                                    </Box>
                                  )}

                                  {/* Selected Badge */}
                                  {isSelected && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        left: 8,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: 24,
                                        height: 24,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 1,
                                      }}
                                    >
                                      <CheckCircleIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                  )}

                                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    {/* Token Icon and Name */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                      {tl.icon ? (
                                        <Avatar 
                                          src={tl.icon} 
                                          sx={{ 
                                            width: 32, 
                                            height: 32, 
                                            mr: 1.5,
                                            border: 1,
                                            borderColor: 'grey.300'
                                          }} 
                                        />
                                      ) : (
                                        <Box
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            mr: 1.5,
                                            bgcolor: 'grey.200',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: 1,
                                            borderColor: 'grey.300',
                                          }}
                                        >
                                          <Typography variant="caption" fontWeight={700} color="text.secondary">
                                            {tl.currency.slice(0, 3)}
                                          </Typography>
                                        </Box>
                                      )}
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                                          {tl.name || tl.currency}
                                        </Typography>
                                        {tl.stablecoin && (
                                          <Chip 
                                            label="Stablecoin" 
                                            size="small" 
                                            color="success"
                                            sx={{ fontSize: '0.6rem', height: 16, mt: 0.5 }} 
                                          />
                                        )}
                                      </Box>
                                    </Box>

                                    {/* Balance */}
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                      Balance: {tl.balance ? parseFloat(tl.balance).toLocaleString() : '0'}
                                    </Typography>

                                    {/* Issuer */}
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary" 
                                      sx={{ 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        mb: 'auto'
                                      }}
                                    >
                                      Issuer: {tl.issuer}
                                    </Typography>

                                    {/* Trust lines count */}
                                    {tl.trustlines && tl.trustlines > 0 && (
                                      <Typography variant="caption" color="primary.main" sx={{ mt: 1 }}>
                                        {tl.trustlines.toLocaleString()} holders
                                      </Typography>
                                    )}
                                  </Box>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No trust-lined tokens found on your address. You can continue to set <b>NFT</b> rules.
                      </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        onClick={nextStep}
                        endIcon={<NavigateNextIcon />}
                        sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600 }}
                      >
                        Continue
                      </Button>
                      {!hasAnyTokenSelection && (
                        <Button
                          onClick={skipStep}
                          startIcon={<NavigateNextIcon />}
                          sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', color: 'text.secondary', '&:hover': { bgcolor: 'grey.100' } }}
                        >
                          Skip Tokens
                        </Button>
                      )}
                      <Button onClick={prevStep} startIcon={<NavigateBeforeIcon />} sx={{ borderRadius: 2, px: 3, py: 1, color: 'text.secondary' }}>
                        Back
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 4: Token requirements & logic */}
              <Step>
                <StepLabel>Set Token Requirements & Logic</StepLabel>
                <StepContent>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Enter minimum amounts per token. You can also <b>skip</b> if you didn’t choose any tokens.
                    </Typography>

                    {hasAnyTokenSelection && (
                      <Stack spacing={2} sx={{ mb: 3 }}>
                        {selectedTokenKeys.map((key) => {
                          const tl = trustLines[key];
                          return (
                            <Card key={key} sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'primary.200', bgcolor: 'primary.50', position: 'relative' }}>
                              {/* LP Badge */}
                              {tl.isLpToken && (
                                <Chip 
                                  label="LP Token" 
                                  size="small" 
                                  color="info"
                                  sx={{ 
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    fontSize: '0.65rem',
                                    height: 20
                                  }} 
                                />
                              )}
                              
                              <Grid container spacing={2} alignItems="center">
                                <Grid item>
                                  {tl.icon ? (
                                    <Avatar src={tl.icon} sx={{ width: 40, height: 40, border: 1, borderColor: 'primary.main' }} />
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        bgcolor: 'grey.200',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: 1,
                                        borderColor: 'primary.main',
                                      }}
                                    >
                                      <Typography variant="caption" fontWeight={700}>
                                        {tl.currency.slice(0, 3)}
                                      </Typography>
                                    </Box>
                                  )}
                                </Grid>
                                <Grid item xs>
                                  <Typography variant="subtitle2" fontWeight={700} noWrap>
                                    {tl.name || tl.currency}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    Issuer: {tl.issuer}
                                  </Typography>
                                  {tl.stablecoin && (
                                    <Chip 
                                      label="Stablecoin" 
                                      size="small" 
                                      color="success"
                                      sx={{ fontSize: '0.6rem', height: 16, ml: 1 }} 
                                    />
                                  )}
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    label="Minimum Amount"
                                    type="number"
                                    fullWidth
                                    value={perTokenMin[key] ?? '1'}
                                    onChange={(e) => setMinForTokenKey(key, e.target.value)}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <FormatListNumberedIcon color="primary" />
                                        </InputAdornment>
                                      ),
                                      inputProps: { min: 0, step: 'any' },
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    helperText={`Required ${tl.name || tl.currency} amount`}
                                  />
                                </Grid>
                              </Grid>
                            </Card>
                          );
                        })}
                      </Stack>
                    )}

                    {selectedTokenKeys.length > 1 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="primary.main" fontWeight={600} mb={1}>
                          Token Logic
                        </Typography>
                        <ToggleButtonGroup
                          exclusive
                          value={tokenLogic}
                          onChange={(_, val) => val && setTokenLogic(val)}
                          sx={{ '& .MuiToggleButton-root': { borderRadius: 2, textTransform: 'none', px: 2.5, py: 1, fontWeight: 600 } }}
                        >
                          <ToggleButton value="AND">Users must meet ALL token requirements (AND)</ToggleButton>
                          <ToggleButton value="OR">Users may meet ANY one token requirement (OR)</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button variant="contained" onClick={nextStep} endIcon={<NavigateNextIcon />} sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600 }}>
                        Continue
                      </Button>
                      {!hasAnyTokenSelection && (
                        <Button onClick={nextStep} startIcon={<NavigateNextIcon />} sx={{ borderRadius: 2, px: 3, py: 1, color: 'text.secondary' }}>
                          Skip
                        </Button>
                      )}
                      <Button onClick={prevStep} startIcon={<NavigateBeforeIcon />} sx={{ borderRadius: 2, px: 3, py: 1, color: 'text.secondary' }}>
                        Back
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 5: Combine rules */}
              <Step>
                <StepLabel>Combine Rules (AND / OR)</StepLabel>
                <StepContent>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Choose how to combine the <b>NFT</b> rules block and the <b>Token</b> rules block.
                    </Typography>

                    <ToggleButtonGroup
                      exclusive
                      value={globalLogic}
                      onChange={(_, val) => val && setGlobalLogic(val)}
                      sx={{ '& .MuiToggleButton-root': { borderRadius: 2, textTransform: 'none', px: 2.5, py: 1, fontWeight: 600 } }}
                    >
                      <ToggleButton value="OR">OR</ToggleButton>
                      <ToggleButton value="AND">AND</ToggleButton>
                    </ToggleButtonGroup>

                    {!canSave && (
                      <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                        {saveDisabledReason}
                      </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button variant="contained" onClick={nextStep} endIcon={<NavigateNextIcon />} sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600 }}>
                        Continue
                      </Button>
                      <Button onClick={prevStep} startIcon={<NavigateBeforeIcon />} sx={{ borderRadius: 2, px: 3, py: 1, color: 'text.secondary' }}>
                        Back
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 6: Kick Message */}
              <Step>
                <StepLabel>Configure Kick Message</StepLabel>
                <StepContent>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      This message will be shown to users who don't meet the requirements when they're removed from the space.
                    </Typography>

                    <TextField
                      label="Kick Message"
                      multiline
                      rows={3}
                      value={kickMessage}
                      onChange={onKickChange}
                      fullWidth
                      placeholder="You need to own the required NFTs/tokens (and meet dynamic rules) to access this space."
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'background.paper' },
                          '&.Mui-focused': { bgcolor: 'background.paper' },
                        },
                      }}
                      helperText="Keep it friendly and informative. Provide a link to where they can get the NFT/token if possible."
                    />

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        onClick={nextStep}
                        endIcon={<NavigateNextIcon />}
                        sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600 }}
                      >
                        Continue
                      </Button>
                      <Button
                        onClick={prevStep}
                        startIcon={<NavigateBeforeIcon />}
                        sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', color: 'text.secondary', '&:hover': { bgcolor: 'grey.100' } }}
                      >
                        Back
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 7: Review & Confirm */}
              <Step>
                <StepLabel>Review & Confirm</StepLabel>
                <StepContent>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Review your token gate configuration and save when ready.
                    </Typography>

                    {botJoinError && (
                      <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                        <Typography variant="body2" fontWeight={600} mb={1}>
                          🤖 TokenGate Bot Invitation Required:
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          {botJoinError}
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          <strong>How to invite:</strong> Use the room settings or type <code>/invite @tokengatebot:synapse.textrp.io</code> in the room chat.
                        </Typography>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          The bot is required to enforce token gate rules and manage member access.
                        </Typography>
                      </Alert>
                    )}

                    {(!nftBlockConfigured && !tokenBlockConfigured) && (
                      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        Add at least one NFT or one Token rule to save.
                      </Alert>
                    )}

                    {(globalLogic === 'AND' && (!nftBlockConfigured || !tokenBlockConfigured)) && (
                      <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        AND logic requires both blocks. Add at least one NFT and one Token rule, or switch Global logic to OR.
                      </Alert>
                    )}

                    {(nftBlockConfigured || tokenBlockConfigured) && (
                      <Card
                        sx={{
                          p: 3,
                          mb: 3,
                          borderRadius: 2,
                          border: 1,
                          borderColor: 'primary.200',
                          bgcolor: 'primary.25',
                          boxShadow: 2,
                        }}
                      >
                        <Typography variant="h6" gutterBottom color="primary.main" fontWeight={700}>
                          Token Gate Configuration
                        </Typography>

                        <Grid container spacing={3}>
                          {/* NFT summary */}
                          {nftBlockConfigured && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="primary.main" fontWeight={600} mb={1}>
                                  NFT Requirements
                                </Typography>
                                <Stack spacing={1}>
                                  {selectedKeys.map((key) => {
                                    const nft = allNFTs[key].nfts[0];
                                    const rows = (attrPairs[key] || []).filter(r => r.key && (r.value ?? '') !== '');
                                    const thisAttrLogic = attrLogic[key] || 'AND';

                                    return (
                                      <Stack key={key} direction="row" spacing={2} alignItems="center">
                                        <Avatar
                                          variant="rounded"
                                          src={nft.imageURI}
                                          sx={{ width: 48, height: 48, border: 2, borderColor: 'primary.main' }}
                                        />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="body2" fontWeight={700} noWrap>
                                            {nft.collectionName || nft.metadata?.name || 'Unnamed Collection'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" noWrap>
                                            Issuer: {nft.issuer} • Taxon: {nft.nftokenTaxon}
                                          </Typography>
                                          {rows.length > 0 && (
                                            <Box sx={{ mt: 0.5 }}>
                                              {rows.map(r => (
                                                <Typography key={r.id} variant="caption" color="primary.main" sx={{ display: 'block' }}>
                                                  Dynamic: {r.key} = <b>{String(r.value)}</b>
                                                </Typography>
                                              ))}
                                              {rows.length > 1 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                  Attribute logic: <b>{thisAttrLogic}</b>
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                        </Box>
                                        <Typography variant="body2" fontWeight={700}>
                                          ≥ {perCollectionCounts[key] ?? 1}
                                        </Typography>
                                      </Stack>
                                    );
                                  })}
                                </Stack>
                                {selectedKeys.length > 1 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    NFT logic: <b>{logicOp}</b>
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          )}

                          {/* Token summary */}
                          {tokenBlockConfigured && (
                            <Grid item xs={12} md={6}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="primary.main" fontWeight={600} mb={1}>
                                  Token Requirements
                                </Typography>
                                <Stack spacing={1}>
                                  {selectedTokenKeys.map((key) => {
                                    const tl = trustLines[key];
                                    return (
                                      <Stack key={key} direction="row" spacing={2} alignItems="center">
                                        {tl.icon ? (
                                          <Avatar src={tl.icon} sx={{ width: 48, height: 48, border: 1, borderColor: 'primary.main' }} />
                                        ) : (
                                          <Box
                                            sx={{
                                              width: 48,
                                              height: 48,
                                              borderRadius: 2,
                                              bgcolor: 'grey.100',
                                              border: 1,
                                              borderColor: 'grey.300',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}
                                          >
                                            <Typography variant="caption" fontWeight={700}>
                                              {tl.currency.slice(0, 4)}
                                            </Typography>
                                          </Box>
                                        )}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="body2" fontWeight={700} noWrap>
                                            {tl.name || tl.currency}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" noWrap>
                                            Issuer: {tl.issuer}
                                          </Typography>
                                          {tl.isLpToken && (
                                            <Chip 
                                              label="LP" 
                                              size="small" 
                                              color="info"
                                              sx={{ fontSize: '0.6rem', height: 16, ml: 1 }} 
                                            />
                                          )}
                                          {tl.stablecoin && (
                                            <Chip 
                                              label="Stablecoin" 
                                              size="small" 
                                              color="success"
                                              sx={{ fontSize: '0.6rem', height: 16, ml: 1 }} 
                                            />
                                          )}
                                        </Box>
                                        <Typography variant="body2" fontWeight={700}>
                                          ≥ {perTokenMin[key] ?? '1'}
                                        </Typography>
                                      </Stack>
                                    );
                                  })}
                                </Stack>
                                {selectedTokenKeys.length > 1 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Token logic: <b>{tokenLogic}</b>
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          )}
                        </Grid>

                        {/* Global logic */}
                        {(nftBlockConfigured && tokenBlockConfigured) && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="primary.main" fontWeight={600} mb={1}>
                              Gate Join Logic (between Token block & NFT block)
                            </Typography>
                            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
                              <Typography variant="body1" sx={{ mt: 0 }}>
                                {globalLogic === 'AND'
                                  ? 'Users must satisfy both the TOKEN block and the NFT block (AND).'
                                  : 'Users may satisfy either the TOKEN block or the NFT block (OR).'}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Card>
                    )}

                    <Box sx={{ mt: 3 }}>
                      {saveError && (
                        <Alert
                          severity="error"
                          sx={{
                            mb: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'error.dark',
                            bgcolor: 'error.50',
                            color: 'error.dark',
                          }}
                        >
                          {saveError}
                        </Alert>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        onClick={handleSaveClick}
                        color="primary"
                        size="large"
                        startIcon={<SaveIcon />}
                        disabled={!canSave}
                        sx={{
                          mr: 1,
                          borderRadius: 2,
                          px: 4,
                          py: 1.5,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: '1.1rem',
                          boxShadow: 3,
                          '&:hover': { boxShadow: 6, transform: 'translateY(-1px)' },
                        }}
                      >
                        Save Configuration
                      </Button>
                      {!canSave && (
                        <Typography variant="body2" color="text.secondary">
                          {saveDisabledReason}
                        </Typography>
                      )}
                      <Button
                        onClick={prevStep}
                        startIcon={<NavigateBeforeIcon />}
                        sx={{ borderRadius: 2, px: 3, py: 1.5, textTransform: 'none', color: 'text.secondary', '&:hover': { bgcolor: 'grey.100' } }}
                      >
                        Back
                      </Button>
                      </Box>
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>

          </Box>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelSave}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Confirm Token Gate Configuration
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description" sx={{ mb: 2 }}>
            Setting up a token gate will charge <strong>5 mCredits</strong> from your account.
          </DialogContentText>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            This fee covers the blockchain verification and enforcement services required to manage your token gate rules.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCancelSave} 
            color="secondary"
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSave} 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!canSave}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            Confirm & Pay 5 mCredits
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeactivateDialog}
        onClose={handleCancelDeactivate}
        aria-labelledby="deactivate-dialog-title"
        aria-describedby="deactivate-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="deactivate-dialog-title" sx={{ fontWeight: 700, color: 'warning.main' }}>
          Disable Token Gate?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="deactivate-dialog-description" sx={{ mb: 2 }}>
            Turning off the token gate will immediately allow anyone to join this room. You can re-enable the rule at any time, but members who join while it's disabled will remain in the room.
          </DialogContentText>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            If you're sure you want to proceed, confirm below. Enforcement will pause until you toggle the gate back on.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelDeactivate}
            color="secondary"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Keep Enabled
          </Button>
          <Button
            onClick={handleConfirmDeactivate}
            variant="contained"
            color="warning"
            disabled={isUpdatingGateActive}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 },
            }}
          >
            Disable Gate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
